"use client";

import { useCallback, useDeferredValue, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { modelsForBrand } from "@/lib/constants";
import { useCatalogNav } from "@/components/cars/CatalogNavContext";
import { CATALOG_FILTER_KEYS } from "@/lib/catalog-filter-seo";

/** Тот же порядок ключей, что в `stableCatalogQueryString` — один canonical в Google. */
function stableCatalogQueryFromParams(p: URLSearchParams): string {
  const map = new Map<string, string>();
  p.forEach((v, k) => {
    const t = v.trim();
    if (t) map.set(k, t);
  });
  const order = CATALOG_FILTER_KEYS as readonly string[];
  const parts: string[] = [];
  for (const key of order) {
    const v = map.get(key);
    if (v !== undefined) parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
  }
  const extras = [...map.keys()].filter((k) => !order.includes(k)).sort();
  for (const key of extras) {
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(map.get(key)!)}`);
  }
  return parts.join("&");
}

/** Навигация по /cars с query: transition + replace, без спама в истории. */
export function useCatalogFilterNavigate(basePath = "/cars", defaultBrand?: string) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { startTransition } = useCatalogNav();

  const navigate = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      const next = new URLSearchParams(searchParams.toString());
      mutate(next);
      const qs = stableCatalogQueryFromParams(next);
      startTransition(() => {
        router.replace(qs ? `${basePath}?${qs}` : basePath, { scroll: false });
      });
    },
    [basePath, router, searchParams, startTransition],
  );

  const setParam = useCallback(
    (key: string, value: string) => {
      // Если меняем бренд на странице бренда - перенаправляем на страницу нового бренда
      if (key === "brand" && defaultBrand && value && value.toLowerCase() !== defaultBrand.toLowerCase()) {
        const next = new URLSearchParams(searchParams.toString());
        const v = value.trim();
        next.delete("brand"); // Удаляем brand из query params
        
        // Очищаем model если он не подходит для нового бренда
        const allowed = modelsForBrand(v);
        const curModel = next.get("model");
        if (curModel && allowed.length > 0 && !allowed.includes(curModel)) {
          next.delete("model");
        }
        
        const qs = stableCatalogQueryFromParams(next);
        // Преобразуем бренд в slug точно как в generateStaticParams
        const brandSlug = v.toLowerCase();
        const newPath = `/cars/brand/${encodeURIComponent(brandSlug)}`;
        startTransition(() => {
          router.push(qs ? `${newPath}?${qs}` : newPath);
        });
        return;
      }
      
      navigate((p) => {
        const v = value.trim();
        
        // На странице бренда не добавляем brand в query params
        if (key === "brand" && defaultBrand) {
          if (!v || v.toLowerCase() === defaultBrand.toLowerCase()) {
            p.delete("brand");
          }
        } else {
          if (v) p.set(key, v);
          else p.delete(key);
        }

        if (key === "brand") {
          const allowed = modelsForBrand(v);
          const curModel = p.get("model");
          if (!v) p.delete("model");
          else if (curModel && allowed.length > 0 && !allowed.includes(curModel)) p.delete("model");
        }
      });
    },
    [navigate, defaultBrand, router, searchParams, startTransition],
  );

  const clearAll = useCallback(() => {
    startTransition(() => {
      router.replace(basePath, { scroll: false });
    });
  }, [basePath, router, startTransition]);

  return { setParam, navigate, clearAll, searchParams };
}

/**
 * Поля цены: мгновенный ввод + запись в URL только когда `useDeferredValue` догнал ввод.
 *
 * Почему не голый `useDeferredValue` без проверки `deferred === local`: тогда effect мог бы
 * дергать router на промежуточных значениях. Почему не `setTimeout`: тот же эффект даёт React
 * Scheduler, без произвольной задержки в ms.
 *
 * (Если нужна именно пауза «тихо N ms» — верните отдельный debounce-хук с таймером.)
 */
export function useDeferredCatalogParam(
  paramKey: string,
  setParam: (key: string, value: string) => void,
  searchParams: ReturnType<typeof useSearchParams>,
) {
  const urlVal = searchParams.get(paramKey) ?? "";
  const [local, setLocal] = useState(urlVal);
  const deferred = useDeferredValue(local);

  useEffect(() => {
    setLocal(urlVal);
  }, [urlVal]);

  useEffect(() => {
    if (deferred !== local) return;

    const next = deferred.trim();
    const cur = (searchParams.get(paramKey) ?? "").trim();
    if (next === cur) return;

    setParam(paramKey, next);
  }, [deferred, local, paramKey, setParam, searchParams]);

  const onChange = useCallback((raw: string) => {
    setLocal(raw);
  }, []);

  return { local, onChange };
}
